import { useCallback, useEffect, useState } from 'react'
import { useAuth as useWorkOSAuth } from '@workos-inc/authkit-react'
import { useHttpInterceptorEffect } from './useHttpInterceptorEffect'
import useConfig from '../useConfig'

type AuthTokenSetupProps = {
  initApiToken: (
    token: string,
    refreshToken: () => Promise<string | undefined>
  ) => Promise<void>
  refreshApiToken: (token: string, logout: () => void) => Promise<void>
  setNoAccess?: () => void
}

export const useAuth = ({
  initApiToken,
  refreshApiToken,
  setNoAccess,
}: AuthTokenSetupProps) => {
  const { getAccessToken, signIn, signOut } = useWorkOSAuth()
  const [isTokenProvided, setIsTokenProvided] = useState(false)
  const { AUTH_REDIRECT_URI } = useConfig()

  const fetchAccessToken = useCallback(async () => {
    try {
      const accessToken = await getAccessToken()
      return accessToken
    } catch (e) {
      console.error(e)
      const searchParams = new URLSearchParams(window.location.search)
      const context = searchParams.get('context') ?? undefined
      await signIn({ context })
    }
  }, [getAccessToken, signIn])

  const logoutCb = useCallback(
    () => signOut({ returnTo: AUTH_REDIRECT_URI }),
    [signOut, AUTH_REDIRECT_URI]
  )

  const refreshAuthToken = useCallback(async () => {
    const token = await fetchAccessToken()
    if (!token) {
      return
    }
    try {
      await refreshApiToken(token, logoutCb)
    } catch (e) {
      console.error(e)
    }
    return token
  }, [fetchAccessToken, refreshApiToken])

  const setAuthToken = useCallback(async () => {
    const token = await fetchAccessToken()
    if (!token) {
      return
    }
    await initApiToken(token, refreshAuthToken)
    setIsTokenProvided(true)
    return token
  }, [fetchAccessToken, initApiToken, refreshAuthToken])

  useHttpInterceptorEffect({
    refreshToken: refreshAuthToken,
    onError: ({ status }) => {
      if (status === 'token_invalid' || !setNoAccess) {
        logoutCb()
      } else {
        setNoAccess()
      }
    },
  })

  useEffect(() => {
    setAuthToken()
  }, [setAuthToken])

  return {
    isTokenProvided,
    refreshToken: refreshAuthToken,
    logout: logoutCb,
  }
}
